import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { TabItem, TabPanel, Tabs } from '@flixbus/honeycomb-react';

import GoBack from 'common/GoBack';
import PageSizer from 'common/PageSizer';
import { useActiveElements } from 'global/ActiveElements/useActiveElements';

import { makeActiveElementsConfig, PAGE_KEY } from './activeElementsConfigFactory';
import { crewMemberActionLogStore } from './components/CrewMemberActionLog/CrewMemberActionLogStore';
import Info from './components/Info';
import personalTimelineStore from './components/PersonalTimeline/PersonalTimelineStore';
import { crewMemberDetailsStore as store } from './crewMemberDetailsStore';
import { TABS_CONFIG } from './tabsConfig';
import { CREW_MEMBER_DETAILS_TEXT } from './text';

import styles from './styles.module.css';

const CrewMemberDetails = observer(() => {
	const { crewId } = useParams();
	const active = useActiveElements();

	useEffect(() => {
		if (!crewId) return;
		store.init(crewId);
	}, [crewId]);

	const { employeeId, currentHomeBase, employmentType } = store;
	const baseTimezone = currentHomeBase?.city?.timezone;
	const employmentTypeId = employmentType?.id ?? null;
	useEffect(() => {
		if (!store.employeeId) return;
		const modalConfig = makeActiveElementsConfig({
			employeeId: store.employeeId,
			baseTimezone: store.currentHomeBase?.city?.timezone,
			currentEmploymentTypeId: store.employmentType?.id ?? null,
			currentHomeBase: store.currentHomeBase ?? null,
			refetchTimelineByDate: personalTimelineStore.checkCurrentWeekAndRefetchTimeline,
			refetchActionsLog: crewMemberActionLogStore.fetchLog,
			refetchCrewMemberInfo: store.fetchAll,
		});

		active.mountForPage(PAGE_KEY, modalConfig);
		return () => active.cleanupPage(PAGE_KEY);
	}, [active, employeeId, baseTimezone, currentHomeBase, employmentTypeId]);

	const tabs = () => {
		const tabKeys = [...TABS_CONFIG.keys()];
		return tabKeys.map((tab) => (
			<TabItem
				key={tab}
				id={TABS_CONFIG.get(tab).tabId}
				panelId={TABS_CONFIG.get(tab).panelId}
				active={TABS_CONFIG.get(tab).tabId === store.activeTab}
				extraClasses={styles.tab}
			>
				<div className={styles.tab}>
					{TABS_CONFIG.get(tab).icon}
					{TABS_CONFIG.get(tab).label}
				</div>
			</TabItem>
		));
	};

	const panels = () => {
		const tabKeys = [...TABS_CONFIG.keys()];
		return tabKeys.map((tab) => (
			<TabPanel
				key={TABS_CONFIG.get(tab).panelId}
				id={TABS_CONFIG.get(tab).panelId}
				tabId={TABS_CONFIG.get(tab).tabId}
			>
				{TABS_CONFIG.get(tab).component}
			</TabPanel>
		));
	};

	// Gate the children on either an in-flight load OR the (very brief) window
	// between first render and the useEffect that calls store.init(crewId).
	// Without `!employeeId` here, child tabs would mount with crewMemberDetailsStore.employeeId === null
	// and fire Apollo queries against `crewMemberId: null`.
	if (store.loading || !employeeId) {
		return (
			<PageSizer fullPage>
				<GoBack label={CREW_MEMBER_DETAILS_TEXT.crewListLabel} fallback="/crew-management" />
				<div className={styles.info}>{CREW_MEMBER_DETAILS_TEXT.loading}</div>
			</PageSizer>
		);
	}
	if (store.error) {
		return (
			<PageSizer fullPage>
				<GoBack label={CREW_MEMBER_DETAILS_TEXT.crewListLabel} fallback="/crew-management" />
				<div className={styles.info}>{CREW_MEMBER_DETAILS_TEXT.loadFailed}</div>
			</PageSizer>
		);
	}

	return (
		<PageSizer fullPage>
			<GoBack label="Crew List" fallback="/crew-management" />

			<div className={styles.info}>
				<Info />
			</div>

			<Tabs
				navRest={{ Elem: 'section', 'aria-label': CREW_MEMBER_DETAILS_TEXT.tabsAriaLabel }}
				tabs={tabs()}
				panels={panels()}
				onTabActivated={(tabId) => store.setActiveTab(tabId)}
			/>
		</PageSizer>
	);
});

export default CrewMemberDetails;
